import { useEffect, useState } from "react";
import {
  COMPLETION_ITEM_LABEL,
  COMPLETION_OUTCOME_LABEL,
  NEXT_FOLLOW_UP_KIND_LABEL,
  NEXT_FOLLOW_UP_KINDS,
  nextFollowUpLine,
  type MonthlyReviewDecisions,
} from "@care-by-ella/contracts";
import {
  CompletionPicker,
  CompletionProgress,
  completionPayload,
  completionProblem,
  EMPTY_COMPLETION,
  type CompletionValue,
} from "@/components/CompletionPicker";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  nextFollowUpBody,
  nextFollowUpDraftChanged,
  nextFollowUpDraftFrom,
  nextFollowUpDraftProblem,
  type NextFollowUpDraft,
} from "@/lib/next-follow-up-draft";
import { labOrderOnlyControl, meetingNeedsLabOrder } from "@/lib/webdoc-step";
import { requestCycleCompletion, setNextFollowUp } from "@/lib/work-queue-api";
import { hiddenWhenReadOnly } from "./SupportView";
import { WebdocControlStep } from "./WebdocControlStep";

// TES-978 — the monthly review's two decisions beside the renewal. Emelie: in
// the same workspace as the first care meeting, Kirill must be able to
//   "fatta dos-, recept-, kompletterings- och uppföljningsbeslut".
// Dose and prescription are the order fields and the renewal buttons; these
// are the other two.

const FIELD =
  "rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:border-brand focus:outline-none";

function shortDay(iso: string): string {
  return iso.slice(0, 10);
}

// "Nästa uppföljning": when, and how. Saved on its own button so the decision
// is explicit and audited; the saved line leads the note's "Nästa uppföljning"
// when it is assembled.
function NextFollowUpEditor({
  patientId,
  cycleId,
  decisions,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  cycleId: string;
  decisions: MonthlyReviewDecisions;
  onSaved: (next: MonthlyReviewDecisions) => void;
  onUnauthorized: () => void;
}) {
  const [draft, setDraft] = useState<NextFollowUpDraft>(() =>
    nextFollowUpDraftFrom(decisions),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saved = decisions.nextFollowUp;
  const problem = nextFollowUpDraftProblem(draft);
  const changed = nextFollowUpDraftChanged(draft, saved);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await setNextFollowUp(patientId, cycleId, nextFollowUpBody(draft)),
      );
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError("Uppföljningen kunde inte sparas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <fieldset className="flex flex-col gap-xs rounded-md border border-surfaceMuted p-sm">
      <legend className="px-xs text-xs font-semibold text-textSecondary">
        Nästa uppföljning
      </legend>
      <div className="flex flex-wrap items-center gap-md">
        {NEXT_FOLLOW_UP_KINDS.map((kind) => (
          <label
            key={kind}
            className="flex cursor-pointer items-center gap-xs text-sm text-textPrimary"
          >
            <input
              type="radio"
              name={`next-follow-up-${cycleId}`}
              className="accent-brand"
              checked={draft.kind === kind}
              disabled={busy}
              onChange={() => setDraft({ ...draft, kind })}
            />
            {NEXT_FOLLOW_UP_KIND_LABEL[kind]}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-sm">
        <label className="flex items-center gap-xs text-xs text-textSecondary">
          Datum
          <input
            type="date"
            value={draft.on}
            onChange={(e) => setDraft({ ...draft, on: e.target.value })}
            disabled={busy}
            className={FIELD}
          />
        </label>
        <input
          type="text"
          value={draft.note}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          disabled={busy}
          maxLength={1000}
          aria-label="Kommentar till uppföljningen"
          placeholder={
            draft.kind === "other"
              ? "Hur ska uppföljningen ske?"
              : "Kommentar (valfritt)"
          }
          className={`min-w-[12rem] flex-1 ${FIELD}`}
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || problem !== null || !changed}
          title={problem ?? undefined}
          className="rounded-md border border-brand px-md py-xs text-sm font-semibold text-brand hover:bg-brand/5 disabled:opacity-50"
        >
          Spara uppföljning
        </button>
      </div>
      {saved ? (
        <p className="text-xs text-textSecondary" aria-live="polite">
          {`Sparad: ${nextFollowUpLine(saved)}`}
          {saved.setByDisplayName ? ` · ${saved.setByDisplayName}` : ""}
          {` · ${shortDay(saved.setAt)}`}
          {changed ? " · ändringen är inte sparad" : ""}
        </p>
      ) : null}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
    </fieldset>
  );
}

// The komplettering, with the ONE picker (TES-950) the first meeting and the
// transfer use. It reaches the patient the way theirs do: her checklist,
// "Komplettera ditt medicinska underlag".
function MonthlyCompletionEditor({
  patientId,
  cycleId,
  decisions,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  cycleId: string;
  decisions: MonthlyReviewDecisions;
  onSaved: (next: MonthlyReviewDecisions) => void;
  onUnauthorized: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<CompletionValue>({
    ...EMPTY_COMPLETION,
    outcome: "completion",
  });
  // TES-1216 — "Provbeställning/remiss för blodprov är skapad i Webdoc",
  // required before a new blood test reaches her checklist.
  const [labOrderDone, setLabOrderDone] = useState(false);
  const [requisitionRef, setRequisitionRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const needsLabOrder = meetingNeedsLabOrder(value);
  const problem =
    completionProblem(value) ??
    (needsLabOrder && !labOrderDone
      ? "Bekräfta att provbeställningen/remissen är skapad i Webdoc."
      : null);
  const requested = decisions.completion;

  // A new request resets the picker for the next one.
  useEffect(() => {
    setValue({ ...EMPTY_COMPLETION, outcome: "completion" });
    setLabOrderDone(false);
    setRequisitionRef("");
  }, [requested?.requestedAt]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await requestCycleCompletion(patientId, cycleId, {
          ...completionPayload(value),
          webdocControl: labOrderOnlyControl({
            needsLabOrder,
            labOrderDone,
            requisitionRef,
          }),
        }),
      );
      setOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError("Kompletteringen kunde inte skickas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-xs">
      <CompletionProgress
        patientId={patientId}
        reloadKey={reloadKey}
        onUnauthorized={onUnauthorized}
      />
      {requested ? (
        <p className="text-xs text-textSecondary">
          {`Komplettering begärd ${shortDay(requested.requestedAt)}: ${requested.items
            .map((i) => COMPLETION_ITEM_LABEL[i])
            .join(", ")}.`}
        </p>
      ) : null}
      {open ? (
        <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
          <CompletionPicker
            patientId={patientId}
            value={value}
            onChange={setValue}
            showOutcome={false}
            disabled={busy}
            onUnauthorized={onUnauthorized}
          />
          <WebdocControlStep
            idPrefix="monthly-completion"
            labOrder={needsLabOrder}
            labOrderDone={labOrderDone}
            onLabOrderDoneChange={setLabOrderDone}
            requisitionRef={{
              value: requisitionRef,
              onChange: setRequisitionRef,
            }}
            disabled={busy}
          />
          <div className="flex flex-wrap items-center gap-sm">
            <button
              type="button"
              onClick={() => void send()}
              disabled={busy || problem !== null}
              title={problem ?? undefined}
              className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              Skicka komplettering till patienten
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="text-sm text-textSecondary underline"
            >
              Avbryt
            </button>
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="self-start rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand"
        >
          {COMPLETION_OUTCOME_LABEL.completion}
        </button>
      )}
    </div>
  );
}

// TES-1040 — hidden in the admin's read-only support view: the next follow-up is a decision the doctor saves.
export const NextFollowUpSection = hiddenWhenReadOnly(NextFollowUpEditor);

// TES-1040 — hidden in the admin's read-only support view: a completion request is sent to the patient.
export const MonthlyCompletionSection = hiddenWhenReadOnly(
  MonthlyCompletionEditor,
);
